import React from 'react';
import PropTypes from 'prop-types';

import { pluralizeObject, Brandfolder } from '@helpers/show_page_helpers';
import { PrimaryButton, TertiaryButton } from '@components/library/button/index';
import { BFLoader } from '@components/common/loader/main';
import CustomFieldsInputRow from '@components/show_page/bulk_actions/sub_components/custom_fields_input_row';

class BulkCustomFieldsModal extends React.Component {
  state = {
    deletedRows: [],
    existingCustomFields: [],
    isLoading: true,
    isSubmitting: false,
    savedCustomFields: {},
    stagedCustomFields: {},
    unsavedChanges: false,
    validFields: false,
    visibleRows: 0,
  }

  componentDidMount() {
    this.fetchExistingCustomFieldKeys();
  }

  updateStagedCustomFields = (position, key, value) => {
    if (key && key.trim().length && value && value.trim().length) {
      this.setState((prevState) => ({
        stagedCustomFields: Object.assign(prevState.stagedCustomFields,
          { [position]: { [key]: value } })
      }), this.validateFields);
    } else {
      this.setState((prevState) => {
        const updatedFields = Object.assign({}, prevState.stagedCustomFields);
        delete updatedFields[position];
        return { stagedCustomFields: updatedFields };
      }, this.validateFields);
    }
  }

  elminateRow = (position) => {
    this.setState((prevState) => {
      const remainingRows = Object.assign({}, prevState.stagedCustomFields);
      delete remainingRows[position];
      return {
        stagedCustomFields: remainingRows,
        deletedRows: prevState.deletedRows.concat(position)
      };
    }, this.validateFields);
  }

  controlledFieldsEnabled = () => (
    (BFG.brandfolderSettings && BFG.brandfolderSettings.controlled_custom_fields_enabled)
  )

  validateFields() {
    const { stagedCustomFields, visibleRows, deletedRows } = this.state;
    const values = Object.values(stagedCustomFields);
    const isValid = (values.length === (visibleRows - deletedRows.length)
      || this.controlledFieldsEnabled()) && values.length > 0;
    this.setState({ validFields: isValid });
    this.checkUnsavedChanges();
  }

  isChanged() {
    const { savedCustomFields, stagedCustomFields } = this.state;

    // if keys (rows) have been updated, return the difference in updated key quantities
    if (Object.keys(savedCustomFields).length !== Object.keys(stagedCustomFields).length) {
      return Math.abs(Object.keys(savedCustomFields).length - Object.keys(stagedCustomFields).length);
    }

    // if values have been updated, return the difference in updated value quantities
    let changeCount = 0;
    Object.keys({ ...savedCustomFields, ...stagedCustomFields }).forEach((savedCustomField) => {
      const savedValue = savedCustomFields[savedCustomField];
      const stagedValue = stagedCustomFields[savedCustomField];
      if ((savedValue && Object.values(savedValue)?.[0]) !== (stagedValue && Object.values(stagedValue)?.[0])) {
        changeCount += 1;
      }
    });

    return changeCount;
  }

  checkUnsavedChanges() {
    const isChanged = this.isChanged();
    if (this.state.unsavedChanges !== !!isChanged) {
      this.setState((prevState) => ({ unsavedChanges: !prevState.unsavedChanges }));
    }
  }

  fetchExistingCustomFieldKeys() {
    let url = `/api/v4/brandfolders/${Brandfolder.key}/custom_field_keys`;
    if (this.props.ugtLocale) {
      url += `?ugt_locale=${this.props.ugtLocale}`;
    }

    $.ajax({
      type: 'GET',
      url,
      contentType: 'application/json',
      headers: { Authorization: `Bearer ${BF_Token}` },
      context: this,
      success: (response) => {
        const filteredKeys = response.data.filter((key) => (!!key.attributes?.name));
        const keysSortedByName = filteredKeys.sort((a, b) => (
          a.attributes.name.toLowerCase() < b.attributes.name.toLowerCase() ? -1 : 1
        ));
        this.setState((prevState) => ({
          existingCustomFields: keysSortedByName,
          isLoading: false,
          visibleRows: this.controlledFieldsEnabled()
            ? keysSortedByName.length
            : prevState.visibleRows + 1
        }));
      },
      error: () => {
        this.setState({ isLoading: false });
      }
    });
  }

  submitCustomFields() {
    this.setState({ isSubmitting: true });
    const { selectedAssetKeys } = this.props;
    const { stagedCustomFields } = this.state;
    const qtyUpdates = this.isChanged();

    const postData = {
      data: {
        asset_keys: Array.from(selectedAssetKeys),
        brandfolder_key: Brandfolder.key,
        custom_fields: Object.values(stagedCustomFields),
        bypass_controlled_custom_fields: false,
      }
    };

    $.ajax({
      type: 'POST',
      url: "/api/v4/bulk_actions/assets/create_custom_fields?queue_priority=high",
      contentType: 'application/json',
      headers: { Authorization: `Bearer ${BF_Token}` },
      data: JSON.stringify(postData),
      context: this,
      success: () => {
        Notify.create({
          type: "success",
          title: t('Processing Custom Field Updates!'),
          body: t(`Updating ${qtyUpdates} custom field${qtyUpdates > 1 ? 's' : ''} for
            ${selectedAssetKeys.size} asset${(selectedAssetKeys.size > 1) ? 's' : ''}`)
        });
        this.setState({ unsavedChanges: false, savedCustomFields: { ...stagedCustomFields } });
      },
      error: () => {
        Notify.create({
          type: "error",
          title: t('Please try again!'),
        });
      },
      complete: () => {
        this.setState({ isSubmitting: false });
      }
    });
  }

  render() {
    const assetQtyObject = pluralizeObject(this.props.selectedAssetKeys);
    const assetCopy = assetQtyObject.plural ? t('assets') : t('asset');
    const {
      deletedRows,
      existingCustomFields,
      isLoading,
      isSubmitting,
      unsavedChanges,
      validFields,
      visibleRows,
    } = this.state;

    return (
      <div className="modal-content-wrapper bulk-custom-fields-modal">
        <div className="modal-content-wrapper__body">
          <h3 className="body-title">
            {t('Add custom fields to')}
            <span className="bold-text">{assetQtyObject.qty} {assetCopy}</span>
          </h3>
          {isLoading ? (
            <BFLoader />
          ) : (
            <div className="labels-container bf-scroll-element">
              <div className="labels-row">
                <span className="row-header">{t('Key')}</span>
                <span className="row-header">{t('Value')}</span>
              </div>
              {[...Array(visibleRows)].map((_, i) => {
                if (deletedRows.includes(i)) { return null; }
                return (
                  <CustomFieldsInputRow
                    key={`custom-fields-inputs-${i}`}
                    existingCustomFields={existingCustomFields}
                    updateStagedCustomFields={this.updateStagedCustomFields}
                    position={i}
                    eliminateRow={this.elminateRow}
                    controlledFields={this.controlledFieldsEnabled()}
                  />
                );
              })}
              {!this.controlledFieldsEnabled() && (
                <div
                  className="add-new-row"
                  onClick={() => this.setState((prevState) => (
                    { visibleRows: prevState.visibleRows + 1 }), this.validateFields)}
                >
                  <span className="bff-plus" />
                  <span>{t('Add another custom field')}</span>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="button-container">
          <TertiaryButton
            className="t-close-modal"
            disabled={isLoading || isSubmitting}
            onClick={this.props.closeModal}
            size="small"
          >
            {t('Close')}
          </TertiaryButton>
          <PrimaryButton
            className="t-confirm-modal"
            disabled={!unsavedChanges || isLoading || !validFields}
            isLoading={isSubmitting}
            onClick={() => this.submitCustomFields()}
            size="small"
          >
            {t('Save')}
          </PrimaryButton>
        </div>
      </div>
    );
  }
}

BulkCustomFieldsModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  selectedAssetKeys: PropTypes.instanceOf(Set).isRequired,
};

export default BulkCustomFieldsModal;
